import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type PointerEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { call, type CallState, type DeviceKind, type Tile } from "../huddle";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { UUID } from "../sync/types";
import { t, tc, tn } from "../i18n";
import { IconChevron, IconCollapse, IconExpand, IconFullView, IconHeadphones, IconLayout, IconLeave, IconMic, IconPin, IconPopOut, IconScreen, IconVideo } from "./icons";
import { PopMenu, type MenuItem } from "./PopMenu";
import { arrange, boxesOf, defaultView, fit, page, type Box, type Layout, type View } from "../huddleLayout";
import { Avatar } from "./ui";

// useCall is the call this tab is in, or null.
export const useCall = () => useSyncExternalStore(call.subscribe, call.snapshot);

// HuddleButton starts or joins a channel's call, from its header; it shows
// how many are in it.
export function HuddleButton({ channel }: { channel: UUID }) {
  const state = useLantern();
  const session = useSession();
  const current = useCall();
  const [error, setError] = useState("");
  if (!state.huddlesOn) return null;
  const huddle = state.huddles.get(channel);
  const here = current?.channel === channel;
  const label = here ? t("You are in this huddle") : huddle ? t("Join the huddle") : t("Start a huddle");
  return (
    <>
      <button
        className={`member-count huddle-button ${huddle ? "live" : ""}`}
        aria-pressed={here}
        disabled={here}
        title={error || label}
        aria-label={huddle ? t("{label}: {n} in it", { label, n: huddle.participants.length }) : label}
        onClick={() => {
          setError("");
          call.join(session, channel).catch((err) => setError(err instanceof Error ? err.message : String(err)));
        }}
      >
        <IconHeadphones size={16} />
        {huddle && huddle.participants.length}
      </button>
      {error && (
        <span className="error huddle-error" role="alert">
          {error}
        </span>
      )}
    </>
  );
}

// letGo gives the focus back after a click, so Space then talks rather
// than pressing the button again; the keyboard keeps its focus.
const letGo = (e: PointerEvent<HTMLButtonElement>) => e.currentTarget.blur();

// Controls are a call's buttons, round and in the middle, as calls have
// them in most apps: the microphone and the camera, each with a menu of
// devices, the screen, and Leave, in red, on the right.
function Controls({ c, win }: { c: CallState; win?: Window }) {
  const toggle = (f: () => Promise<void>) => () => void f().catch(() => {});
  return (
    <div className="huddle-controls" role="group" aria-label={t("Call controls")}>
      <span className="control-pair">
        <button className={`round ${c.muted ? "off" : "on"}`} onClick={toggle(() => call.setMuted(!c.muted))} onPointerUp={letGo} title={c.muted ? t("Unmute") : t("Mute")} aria-label={c.muted ? t("Unmute") : t("Mute")}>
          <IconMic off={c.muted} size={18} />
        </button>
        <DeviceMenu kinds={["audioinput", "audiooutput"]} c={c} label={t("Microphone and speaker")} win={win} />
      </span>
      <span className="control-pair">
        <button className={`round ${c.camera ? "on" : ""}`} disabled={c.connecting} onClick={toggle(() => call.setCamera(!c.camera))} onPointerUp={letGo} title={c.camera ? t("Turn off camera") : t("Turn on camera")} aria-label={c.camera ? t("Turn off camera") : t("Turn on camera")}>
          <IconVideo off={!c.camera} size={18} />
        </button>
        <DeviceMenu kinds={["videoinput"]} c={c} label={t("Camera")} win={win} />
      </span>
      <button className={`round ${c.screen ? "on" : ""}`} aria-pressed={c.screen} disabled={c.connecting} onClick={toggle(() => call.setScreen(!c.screen))} onPointerUp={letGo} title={c.screen ? t("Stop sharing") : t("Share your screen")} aria-label={c.screen ? t("Stop sharing") : t("Share your screen")}>
        <IconScreen size={18} />
      </button>
      <button className="leave" onClick={toggle(() => call.leave())} title={t("Leave the huddle")} aria-label={t("Leave the huddle")}>
        <IconLeave size={18} />
        <span>{t("Leave")}</span>
      </button>
    </div>
  );
}

// DeviceMenu is the little menu beside the microphone or camera that picks
// which one, as Zoom's carets do. Devices come from the browser when it
// opens, so one plugged in since is there.
function DeviceMenu({ kinds, c, label, win }: { kinds: DeviceKind[]; c: CallState; label: string; win?: Window }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [lists, setLists] = useState<MediaDeviceInfo[][]>([]);
  const close = useCallback(() => setAnchor(null), []);
  const heading: Record<DeviceKind, string> = { audioinput: t("Microphone"), audiooutput: tc("device", "Speaker"), videoinput: t("Camera") };
  async function open(at: DOMRect) {
    setAnchor(at);
    setLists(await Promise.all(kinds.map((k) => call.devicesOf(k))));
  }
  const items: MenuItem[] = kinds.flatMap((k, n) => {
    const list = lists[n] ?? [];
    // A browser that cannot choose a speaker lists none.
    if (list.length === 0) return [];
    const chosen = c.devices[k] ?? list.find((d) => d.deviceId === "default")?.deviceId ?? list[0].deviceId;
    return [
      { label: heading[k], heading: true, divider: n > 0 },
      ...list.map((d, i) => ({
        label: d.label || t("{kind} {n}", { kind: heading[k], n: i + 1 }),
        checked: d.deviceId === chosen,
        run: () => void call.setDevice(k, d.deviceId).catch(() => {}),
      })),
    ];
  });
  return (
    <>
      <button
        className="caret"
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        onClick={(e) => (anchor ? close() : void open(e.currentTarget.getBoundingClientRect()))}
        title={t("Choose: {what}", { what: label })}
        aria-label={t("Choose: {what}", { what: label })}
      >
        <IconChevron size={12} />
      </button>
      {anchor && (
        <PopMenu
          anchor={anchor}
          label={label}
          onClose={close}
          win={win}
          items={items.length ? items : [{ label: lists.length ? t("No devices found") : t("Loading…"), heading: true }]}
        />
      )}
    </>
  );
}

// useClock is how long one has been in the call, as 4:05 or 1:02:09.
function useClock(since: number | null): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!since) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  if (!since) return "";
  const s = Math.max(0, Math.floor((now - since) / 1000));
  const two = (n: number) => String(n).padStart(2, "0");
  return s >= 3600 ? `${Math.floor(s / 3600)}:${two(Math.floor(s / 60) % 60)}:${two(s % 60)}` : `${Math.floor(s / 60)}:${two(s % 60)}`;
}

// useTalkKeys are the call's keys, in Lantern's window and in a call's own:
// Ctrl or Cmd+Shift+Space mutes and unmutes anywhere, and, while muted,
// holding Space talks, as in Zoom, unless one is typing. Talking stops when
// Space comes up, or the window is left or hidden: a microphone never
// stays on behind one's back.
function useTalkKeys(c: CallState | null, popped: Window | null) {
  const [held, setHeld] = useState(false);
  const holding = useRef(false);
  const muted = c?.muted ?? false;
  const inCall = !!c && !c.connecting;
  useEffect(() => {
    const release = () => {
      if (!holding.current) return;
      holding.current = false;
      setHeld(false);
      void call.setMuted(true).catch(() => {});
    };
    if (!inCall) {
      holding.current = false;
      setHeld(false);
      return;
    }
    const down = (e: KeyboardEvent) => {
      if (e.isComposing || e.repeat || e.code !== "Space") return;
      if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        void call.setMuted(!muted).catch(() => {});
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || !muted || holding.current) return;
      const at = (e.target as Element | null)?.ownerDocument.activeElement ?? null;
      // Space on a button, a link or a menu presses it.
      if (typing(at) || at?.closest("button, a, select, [role=button], [role=menuitem], [role=menuitemradio], [role=menuitemcheckbox]")) return;
      e.preventDefault();
      holding.current = true;
      setHeld(true);
      void call.setMuted(false).catch(() => {});
    };
    const up = (e: KeyboardEvent) => e.code === "Space" && release();
    const hidden = () => document.visibilityState === "hidden" && release();
    const wins = popped ? [window, popped] : [window];
    for (const w of wins) {
      w.addEventListener("keydown", down);
      w.addEventListener("keyup", up);
      w.addEventListener("blur", release);
      w.addEventListener("pagehide", release);
    }
    document.addEventListener("visibilitychange", hidden);
    return () => {
      for (const w of wins) {
        w.removeEventListener("keydown", down);
        w.removeEventListener("keyup", up);
        w.removeEventListener("blur", release);
        w.removeEventListener("pagehide", release);
      }
      document.removeEventListener("visibilitychange", hidden);
    };
  }, [inCall, muted, popped]);
  return held;
}

// A corner is where the call window sits, snapped there after a drag, as
// FaceTime's does; it is kept in this browser.
type Corner = "tl" | "tr" | "bl" | "br";
const cornerKey = "lantern.call-corner";
const corners: Corner[] = ["tl", "tr", "bl", "br"];
// The top right, below a channel's header, is where it covers least.
const defaultCorner: Corner = "tr";

function savedCorner(): Corner {
  try {
    const v = localStorage.getItem(cornerKey) as Corner | null;
    return v && corners.includes(v) ? v : defaultCorner;
  } catch {
    return defaultCorner;
  }
}

function saveCorner(at: Corner) {
  try {
    localStorage.setItem(cornerKey, at);
  } catch {
    // Private windows may refuse; the corner lasts until reload.
  }
}

// phone is whether the window lies across the top of a phone's screen,
// where it does not move.
const phone = () => matchMedia("(max-width: 700px)").matches;

// margin is the call window's distance from the chat area's edges.
const margin = 12;

// cornerSpot is where a window of a size goes in a corner of the chat area:
// clear of the page's header at the top and the composer at the bottom.
function cornerSpot(at: Corner, main: HTMLElement, w: number, h: number) {
  const r = main.getBoundingClientRect();
  const head = main.querySelector(".channel-header, .page-header")?.getBoundingClientRect();
  const composer = main.querySelector(".composer")?.getBoundingClientRect();
  const top = (head ? head.bottom : r.top) + margin;
  const bottom = (composer ? composer.top : r.bottom) - margin;
  return {
    left: at.endsWith("l") ? r.left + margin : Math.max(r.left, r.right - margin - w),
    top: at.startsWith("t") ? top : Math.max(top, bottom - h),
  };
}

// nearest is the corner nearest a window dropped at a spot.
function nearest(x: number, y: number, w: number, h: number, main: HTMLElement): Corner {
  const r = main.getBoundingClientRect();
  const right = x + w / 2 > r.left + r.width / 2;
  const below = y + h / 2 > r.top + r.height / 2;
  return `${below ? "b" : "t"}${right ? "r" : "l"}` as Corner;
}

// CallWindow is the call one is in, wherever one is in Lantern: a window
// over the chat with who is in it, or their videos and screens, laid out
// as one chooses (huddleLayout.ts), and the controls under them, as Zoom,
// FaceTime and Meet have them. It sits in a corner of the chat area, and a
// drag moves it to another (a double-click puts it back); it shrinks to a
// pill, fills the chat, or pops out into a window of its own. Full, the
// controls lie over the video and fade while the pointer is still.
export function CallWindow() {
  const session = useSession();
  const state = useLantern();
  const c = useCall();
  const [mini, setMini] = useState(false);
  const [full, setFull] = useState(false);
  const [corner, setCorner] = useState<Corner>(savedCorner);
  const [dragAt, setDragAt] = useState<{ left: number; top: number } | null>(null);
  const [spot, setSpot] = useState<{ left: number; top: number; width?: number; height?: number } | null>(null);
  const [popped, setPopped] = useState<Window | null>(null);
  const [view, setView] = useState<View>(savedView);
  const [pinned, setPinned] = useState<string | null>(null);
  const [idle, setIdle] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const drag = useRef<{ dx: number; dy: number; x: number; y: number; moved: boolean } | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useTalkKeys(c, popped);
  const clock = useClock(c?.since ?? null);
  const tiles = c?.tiles ?? [];
  const video = tiles.length > 0;
  const sharing = tiles.filter((x) => x.screen).length;
  useEffect(() => {
    // A screen shared, or a video, brings the window back from a pill.
    if (sharing) setMini(false);
  }, [sharing]);
  useEffect(() => saveCorner(corner), [corner]);
  useEffect(() => saveView(view), [view]);
  // Each call starts with nothing pinned, as a window, not full.
  useEffect(() => {
    setPinned(null);
    setFull(false);
  }, [c?.channel]);
  // The window's place: its corner of the chat area, or all of it when
  // full, kept as the area, the page and the window itself change size.
  useEffect(() => {
    const el = ref.current;
    const main = el?.closest("main");
    if (!el || !main || dragAt) return;
    const measure = () => {
      const next = full
        ? (({ left, top, width, height }) => ({ left, top, width, height }))(main.getBoundingClientRect())
        : cornerSpot(corner, main, el.offsetWidth, el.offsetHeight);
      setSpot((was) => (was && Object.entries(next).every(([k, v]) => was[k as keyof typeof was] === v) ? was : next));
    };
    measure();
    const seen = new ResizeObserver(measure);
    seen.observe(main);
    seen.observe(el);
    for (const part of main.querySelectorAll(".composer, .channel-header, .page-header")) seen.observe(part);
    addEventListener("resize", measure);
    return () => {
      seen.disconnect();
      removeEventListener("resize", measure);
    };
    // Another page has its own header and composer, if any.
  }, [full, corner, dragAt, !!c, mini, video, popped, location.pathname]);
  // With video, the controls fade a few seconds after the pointer stops.
  const wake = () => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    if (full) idleTimer.current = setTimeout(() => setIdle(true), 3000);
  };
  useEffect(() => {
    wake();
    return () => clearTimeout(idleTimer.current);
  }, [full]);
  // The call over, or the page gone, its window closes.
  useEffect(() => {
    if (!c) popped?.close();
  }, [c, popped]);
  useEffect(() => () => popped?.close(), [popped]);
  if (!c) return null;
  const where = session.channelName(c.channel);
  const pin = (key: string) => setPinned((p) => (p === key ? null : key));
  const people = state.huddles.get(c.channel)?.participants ?? c.members.map((m) => m.id);
  const status = c.connecting ? t("Connecting…") : c.calling ? t("Calling…") : clock;
  const title = (
    <span className="call-title">
      <IconHeadphones size={14} />
      <button className="link" onClick={() => navigate(`/c/${c.channel}`)} title={t("Go to {channel}", { channel: where })}>
        {where}
      </button>
      {status && <span className="call-clock">{status}</span>}
    </span>
  );
  if (popped)
    return createPortal(
      <section className="call-window popped" aria-label={t("Huddle in {channel}", { channel: where })}>
        <header>
          {title}
          <span className="call-tools">
            <ViewMenu view={view} onChange={setView} win={popped} />
            <button className="ghost" onClick={() => popped.close()} title={t("Back to Lantern")} aria-label={t("Back to Lantern")}>
              <IconPopOut size={14} />
            </button>
          </span>
        </header>
        {video ? <Stage c={c} view={view} pinned={pinned} onPin={pin} big /> : <People c={c} people={people} />}
        <Controls c={c} win={popped} />
      </section>,
      popped.document.body,
    );
  const open = async () => {
    try {
      const w = await popOut(t("Huddle in {channel}", { channel: where }));
      w.addEventListener("pagehide", () => setPopped(null), { once: true });
      setPopped(w);
    } catch {
      // Refused, as without a click: the call stays on the page.
    }
  };
  const place = dragAt ?? spot ?? undefined;
  const kind = full ? "full" : mini ? "mini" : video ? "video" : "audio";
  return (
    <section
      ref={ref}
      className={`call-window ${kind} at-${corner} ${dragAt ? "dragging" : ""} ${idle && full ? "idle" : ""}`}
      style={place}
      aria-label={t("Huddle in {channel}", { channel: where })}
      onPointerMove={wake}
      onFocus={wake}
    >
      <header
        title={full ? undefined : t("Drag to another corner; double-click to put back")}
        onPointerDown={(e) => {
          if (full || e.button !== 0 || phone() || (e.target as Element).closest("button") || !ref.current) return;
          const r = ref.current.getBoundingClientRect();
          drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, x: e.clientX, y: e.clientY, moved: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drag.current || !ref.current) return;
          // A double-click's jitter is no move.
          if (!drag.current.moved && Math.hypot(e.clientX - drag.current.x, e.clientY - drag.current.y) < 5) return;
          drag.current.moved = true;
          const w = ref.current.offsetWidth;
          const h = ref.current.offsetHeight;
          setDragAt({
            left: Math.max(0, Math.min(e.clientX - drag.current.dx, innerWidth - w)),
            top: Math.max(0, Math.min(e.clientY - drag.current.dy, innerHeight - h)),
          });
        }}
        onPointerUp={() => {
          const el = ref.current;
          const main = el?.closest("main");
          if (drag.current?.moved && dragAt && el && main) setCorner(nearest(dragAt.left, dragAt.top, el.offsetWidth, el.offsetHeight, main));
          drag.current = null;
          setDragAt(null);
        }}
        onPointerCancel={() => {
          drag.current = null;
          setDragAt(null);
        }}
        onLostPointerCapture={() => {
          if (!drag.current) return;
          drag.current = null;
          setDragAt(null);
        }}
        onDoubleClick={(e) => !full && !(e.target as Element).closest("button") && setCorner(defaultCorner)}
      >
        {title}
        <span className="call-tools">
          {!mini && video && <ViewMenu view={view} onChange={setView} />}
          {!mini && (
            <button className="ghost" aria-pressed={full} onClick={() => setFull(!full)} title={full ? t("Exit full view") : t("Full view")} aria-label={full ? t("Exit full view") : t("Full view")}>
              <IconFullView size={14} />
            </button>
          )}
          {pip && !mini && (
            <button className="ghost" onClick={() => void open()} title={t("Pop out")} aria-label={t("Pop out")}>
              <IconPopOut size={14} />
            </button>
          )}
          {!full && (
            <button className="ghost" onClick={() => setMini(!mini)} title={mini ? t("Make larger") : t("Make smaller")} aria-label={mini ? t("Make larger") : t("Make smaller")}>
              {mini ? <IconExpand size={14} /> : <IconCollapse size={14} />}
            </button>
          )}
        </span>
      </header>
      {mini ? (
        <div className="call-mini">
          <People c={c} people={people} small />
          <button className={`round small ${c.muted ? "off" : "on"}`} onClick={() => void call.setMuted(!c.muted).catch(() => {})} title={c.muted ? t("Unmute") : t("Mute")} aria-label={c.muted ? t("Unmute") : t("Mute")}>
            <IconMic off={c.muted} size={16} />
          </button>
          <button className="leave small" onClick={() => void call.leave().catch(() => {})} title={t("Leave the huddle")} aria-label={t("Leave the huddle")}>
            <IconLeave size={16} />
          </button>
        </div>
      ) : (
        <>
          {video || full ? <Stage c={c} view={view} pinned={pinned} onPin={pin} big={full} /> : <People c={c} people={people} />}
          {c.error === "microphone" && <p className="hint">{t("Lantern may not use your microphone; allow it in the browser's site settings.")}</p>}
          {c.error === "device" && <p className="hint">{t("That device could not be used; it may be unplugged or in use.")}</p>}
          {held && <p className="call-talking">{t("Talking while you hold Space")}</p>}
          <Controls c={c} />
        </>
      )}
    </section>
  );
}

// People are who is in a call without video: their photos, with a ring
// while they talk and a mark when their microphone is off.
function People({ c, people, small }: { c: CallState; people: UUID[]; small?: boolean }) {
  const session = useSession();
  const off = new Set(c.members.filter((m) => m.micOff).map((m) => m.id));
  const shown = small ? people.slice(0, 4) : people.slice(0, 12);
  return (
    <div className={`huddle-people ${small ? "small" : ""}`} aria-label={tn(people.length, "{n} person in the huddle", "{n} people in the huddle")}>
      {shown.map((id) => (
        <span key={id} className={`huddle-person ${c.speaking.includes(id) ? "speaking" : ""}`} title={session.name(id)}>
          <Avatar id={id} name={session.name(id)} size={small ? 26 : 44} />
          {off.has(id) && (
            <span className="person-muted" aria-label={t("{name}'s microphone is off", { name: session.name(id) })}>
              <IconMic off size={10} />
            </span>
          )}
          {!small && <span className="person-name">{session.name(id)}</span>}
        </span>
      ))}
      {people.length > shown.length && <span className="huddle-more">+{people.length - shown.length}</span>}
    </div>
  );
}

// pipWindows opens Document Picture-in-Picture windows, where the browser
// has them (Chrome and Edge): a window of its own that stays on top.
const pip = (globalThis as { documentPictureInPicture?: { requestWindow(o: { width: number; height: number }): Promise<Window> } }).documentPictureInPicture;

// popOut opens a window for the call, with the page's styles and theme.
async function popOut(title: string): Promise<Window> {
  const w = await pip!.requestWindow({ width: 480, height: 360 });
  const d = w.document;
  for (const a of Array.from(document.documentElement.attributes)) d.documentElement.setAttribute(a.name, a.value);
  for (const sheet of Array.from(document.styleSheets)) {
    // Linked, as the page's own policy allows no inline styles.
    if (sheet.href) {
      const link = d.createElement("link");
      link.rel = "stylesheet";
      link.href = sheet.href;
      d.head.append(link);
      continue;
    }
    try {
      const style = d.createElement("style");
      style.textContent = Array.from(sheet.cssRules, (r) => r.cssText).join("\n");
      d.head.append(style);
    } catch {
      // A sheet that cannot be read is left out.
    }
  }
  d.title = title;
  d.body.className = "huddle-window";
  return w;
}

const viewKey = "lantern.huddle-view";

function savedView(): View {
  try {
    const v = JSON.parse(localStorage.getItem(viewKey) ?? "null");
    return { ...defaultView, ...(v && typeof v === "object" ? v : {}) };
  } catch {
    return defaultView;
  }
}

function saveView(v: View) {
  try {
    localStorage.setItem(viewKey, JSON.stringify(v));
  } catch {
    // Private windows may refuse; the view lasts until reload.
  }
}

// useSpeaker is whoever else is talking, held for a moment before another
// takes their place, so the large box does not flicker between voices.
function useSpeaker(speaking: UUID[], me: UUID | undefined): UUID | null {
  const [current, setCurrent] = useState<UUID | null>(null);
  const next = speaking.find((id) => id !== me) ?? null;
  useEffect(() => {
    if (!next || next === current) return;
    if (!current) return setCurrent(next);
    const timer = setTimeout(() => setCurrent(next), 1500);
    return () => clearTimeout(timer);
  }, [next, current]);
  return current;
}

// useSize is an element's size, as it changes.
function useSize(ref: RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const win = el.ownerDocument.defaultView ?? window;
    const seen = new win.ResizeObserver(() => setSize({ width: el.clientWidth, height: el.clientHeight }));
    seen.observe(el);
    return () => seen.disconnect();
  }, [ref]);
  return size;
}

// ViewMenu chooses the layout, and what the stage leaves out.
function ViewMenu({ view, onChange, win }: { view: View; onChange: (v: View) => void; win?: Window }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const close = useCallback(() => setAnchor(null), []);
  const set = (patch: Partial<View>) => onChange({ ...view, ...patch });
  const layouts: [Layout, string][] = [
    ["auto", t("Auto")],
    ["speaker", t("Speaker")],
    ["gallery", t("Gallery")],
  ];
  return (
    <>
      <button className="ghost" aria-haspopup="menu" aria-expanded={!!anchor} onClick={(e) => setAnchor(anchor ? null : e.currentTarget.getBoundingClientRect())} title={t("Layout")} aria-label={t("Layout")}>
        <IconLayout size={14} />
      </button>
      {anchor && (
        <PopMenu
          anchor={anchor}
          label={t("Layout")}
          onClose={close}
          win={win}
          items={[
            ...layouts.map(([layout, label]) => ({ label, checked: view.layout === layout, run: () => set({ layout }) })),
            { label: t("Two people side by side"), toggle: true, checked: view.sideBySide, divider: true, run: () => set({ sideBySide: !view.sideBySide }) },
            { label: t("Hide my video"), toggle: true, checked: view.hideSelf, run: () => set({ hideSelf: !view.hideSelf }) },
            { label: t("Hide people without video"), toggle: true, checked: view.hideNoVideo, run: () => set({ hideNoVideo: !view.hideNoVideo }) },
          ]}
        />
      )}
    </>
  );
}

// Stage lays the call's boxes out in the space it has: a grid, paged when
// there are more than fit (9 in the panel, 25 larger); one large with the
// rest in a strip, beside it when the space is wide; or one filling it with
// oneself in a corner.
function Stage({ c, view, pinned, onPin, big }: { c: CallState; view: View; pinned: string | null; onPin: (key: string) => void; big?: boolean }) {
  const body = useRef<HTMLDivElement>(null);
  const size = useSize(body);
  const me = c.members.find((m) => m.me)?.id;
  const speaker = useSpeaker(c.speaking, me);
  const [at, setAt] = useState(0);
  const a = arrange(boxesOf(c.members, c.tiles), view, pinned, speaker);
  const box = (b: Box, extra = "") => <BoxView key={b.key} box={b} extra={extra} speaking={c.speaking.includes(b.who)} pinned={pinned === b.key} onPin={() => onPin(b.key)} />;
  let inner: ReactNode;
  let kind: string = a.kind;
  if (a.kind === "grid") {
    const p = page(a.boxes, big ? 25 : 9, at);
    const pagerHeight = p.pages > 1 ? 32 : 0;
    const f = fit(p.boxes.length, size.width - 12, size.height - 12 - pagerHeight);
    inner = (
      <>
        <div className="huddle-grid" style={size.width && f.width > 0 ? { gridTemplateColumns: `repeat(${f.columns}, ${f.width}px)` } : undefined}>
          {p.boxes.map((b) => box(b))}
        </div>
        {p.pages > 1 && (
          <nav className="huddle-pages" aria-label={t("Pages")}>
            <button className="ghost" disabled={p.at === 0} onClick={() => setAt(p.at - 1)} aria-label={t("Previous page")}>
              ‹
            </button>
            <span>{t("Page {n} of {pages}", { n: p.at + 1, pages: p.pages })}</span>
            <button className="ghost" disabled={p.at === p.pages - 1} onClick={() => setAt(p.at + 1)} aria-label={t("Next page")}>
              ›
            </button>
          </nav>
        )}
      </>
    );
  } else if (a.kind === "pip") {
    inner = (
      <>
        {box(a.main, "main")}
        {box(a.pip, "pip")}
      </>
    );
  } else {
    kind += size.width > size.height * 1.4 ? " beside" : " below";
    inner = (
      <>
        <div className="huddle-main">{box(a.main, "main")}</div>
        <div className="huddle-strip">{a.strip.map((b) => box(b))}</div>
      </>
    );
  }
  return (
    <div ref={body} className={`huddle-body ${kind}`}>
      {inner}
    </div>
  );
}

// BoxView is one box: a person's camera, or their photo with a ring while
// they talk; or a screen. It can be pinned large.
function BoxView({ box, extra, speaking, pinned, onPin }: { box: Box; extra: string; speaking: boolean; pinned: boolean; onPin: () => void }) {
  const session = useSession();
  const name = session.name(box.who);
  const label = box.screen ? t("{name}'s screen", { name }) : box.me ? t("You") : name;
  return (
    <figure className={`huddle-tile ${box.screen ? "screen" : ""} ${box.track ? "" : "face"} ${speaking && !box.screen ? "speaking" : ""} ${extra}`}>
      {box.track ? <Video track={box.track} mirror={box.me && !box.screen} /> : <Avatar id={box.who} name={name} size={extra === "pip" ? 32 : 56} />}
      <figcaption>
        {box.micOff && !box.screen && <IconMic off size={12} />} {label}
      </figcaption>
      <button
        className="huddle-pin"
        aria-pressed={pinned}
        onClick={onPin}
        title={pinned ? tc("huddle", "Unpin") : tc("huddle", "Pin")}
        aria-label={pinned ? tc("huddle", "Unpin {name}", { name: label }) : tc("huddle", "Pin {name}", { name: label })}
      >
        <IconPin size={13} />
      </button>
    </figure>
  );
}

function Video({ track, mirror }: { track: Tile["track"]; mirror: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [track]);
  return <video ref={ref} className={mirror ? "mirror" : ""} autoPlay playsInline muted />;
}

// HuddleMark marks a channel in the sidebar while a call goes on in it.
export function HuddleMark({ channel }: { channel: UUID }) {
  const state = useLantern();
  const h = state.huddles.get(channel);
  if (!h) return null;
  const label = tn(h.participants.length, "Huddle: {n} person", "Huddle: {n} people");
  return (
    <span className="huddle-mark" title={label} aria-label={label}>
      <IconHeadphones size={13} />
    </span>
  );
}

// IncomingCall rings for a DM's call one is being rung for, with Join and
// Decline, over whatever page is open. It takes the focus, unless one is
// typing, where Enter would answer; Escape declines.
export function IncomingCall() {
  const session = useSession();
  const { ringing, channels } = useLantern();
  const current = useCall();
  const join = useRef<HTMLButtonElement>(null);
  const channel = ringing?.channel;
  useEffect(() => {
    if (channel && !typing(document.activeElement)) join.current?.focus();
  }, [channel]);
  if (!ringing) return null;
  const name = session.name(ringing.from);
  const group = channels.get(ringing.channel)?.channel.kind === "group_dm";
  const busy = current && current.channel !== ringing.channel;
  return (
    <section
      className="incoming-call"
      role="alertdialog"
      aria-label={t("{name} is calling you", { name })}
      aria-describedby="incoming-call-what"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          session.declineCall();
        }
      }}
    >
      <Avatar id={ringing.from} name={name} size={40} />
      <div className="incoming-who">
        <strong>{name}</strong>
        <span id="incoming-call-what">
          {group ? t("Huddle in {channel}", { channel: session.channelName(ringing.channel) }) : t("Calling you…")}
          {busy && " " + t("Joining leaves your huddle in {channel}.", { channel: session.channelName(current.channel) })}
        </span>
      </div>
      <button className="decline" onClick={() => session.declineCall()} title={t("Decline")} aria-label={t("Decline")}>
        <IconLeave size={18} />
      </button>
      <button ref={join} className="answer" onClick={() => session.answerCall()} title={t("Join")} aria-label={t("Join")}>
        <IconHeadphones size={18} />
      </button>
    </section>
  );
}

// typing is whether the focus is where one writes.
function typing(el: Element | null) {
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || (el as HTMLElement).isContentEditable);
}
